import { useSelector } from '@xstate/react';
import { useFormContext } from 'react-hook-form';

import { SimpleTooltip } from '@eluve/components';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  FormControl,
  FormField,
  FormItem,
  Icon,
  Input,
  Stack,
  cn,
} from '@eluve/components';

import { BlockTypeIcon } from './BlockTypeIcon';
import { templateBuilderStore } from './templateBuilderStore';
import { FlattenedBlock } from './utilities';

const LabelPlaceholder: Partial<Record<FlattenedBlock['type'], string>> = {
  group: 'Group name',
  text: 'Text block name',
  checkbox: 'Checkbox name',
};

interface TreeItemHeaderProps {
  handleProps?: React.HTMLAttributes<HTMLDivElement>;
  collapsed?: boolean;
  block: FlattenedBlock;
}

export const TreeItemHeader: React.FC<TreeItemHeaderProps> = ({
  handleProps,
  collapsed,
  block,
}) => {
  const { control } = useFormContext();

  const isReadonly = useSelector(
    templateBuilderStore,
    (store) => store.context.isReadOnly,
  );

  const isImported = useSelector(
    templateBuilderStore,
    (store) => store.context.isImported,
  );

  return (
    <Stack
      className="border-b bg-bg-secondary p-5"
      justify="between"
      gap={4}
      direction="horizontal"
    >
      <Stack gap={4} direction="horizontal">
        {!isReadonly && !isImported && (
          <div {...handleProps}>
            <Icon name="GripVertical" />
          </div>
        )}
        <SimpleTooltip label={collapsed ? 'Expand' : 'Collapse'}>
          <Button
            variant="outline"
            onClick={() =>
              templateBuilderStore.send({
                type: 'toggleCollapse',
                id: block.id,
                collapsed: !collapsed,
              })
            }
          >
            <Icon
              name={!collapsed ? 'ChevronsDownUp' : 'ChevronsUpDown'}
              size="auto"
            />
          </Button>
        </SimpleTooltip>
        <BlockTypeIcon type={block.type} />
        <FormField
          control={control}
          name="label"
          render={({ field, fieldState: { error } }) => (
            <FormItem className="flex-1">
              <FormControl>
                <Input
                  placeholder={LabelPlaceholder[block.type]}
                  {...field}
                  disabled={isReadonly || isImported}
                  className={cn(error && 'border-border-negative-strong')}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </Stack>
      {!isReadonly && !isImported && (
        <AlertDialog>
          <SimpleTooltip label="Remove block">
            <AlertDialogTrigger asChild>
              <Button variant="outline">
                <Icon name="Trash" size="auto" />
              </Button>
            </AlertDialogTrigger>
          </SimpleTooltip>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                Removing this block cannot be reversed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                onClick={() =>
                  templateBuilderStore.send({
                    type: 'removeBlock',
                    id: block.id,
                  })
                }
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </Stack>
  );
};
